// Preload de los VIDEOS de una tanda de la columna de resultados —
// ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna.
//
// "Animar" desde el visor prestado aterriza en la columna como una tanda más, y la
// tanda se cierra con lo que el preload resuelve. `preloadGeneratedImages` no sirve
// para un video: firma `opt/<nombre>.mp4`, que no existe — el thumbnail que escribe el
// backend para un video es `opt/<nombre>.png` (con `full/<nombre>.png` como respaldo),
// y eso es lo que la card terminada tiene que mostrar debajo del play. El `.mp4` va en
// `signedUrl` para quien quiera reproducirlo, pero lo que se precalienta en la cache
// del navegador es el PNG: es lo único que se dibuja en la grilla.
//
// Misma derivación de rutas que `fetchImages` en `GeneratedImagesHistory` (video →
// thumbnail `.png`); `rutasThumbnailVideo` es puro y se testea en
// `preloadVideosDeTanda.test.ts`.

import { supabase } from '../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from './imageCache';
import { mapWithConcurrency } from './concurrency';
import type { PreloadedImage } from './preloadGeneratedImages';

/** Mismo tope que `preloadGeneratedImages`: cada operación de Storage toma una
 *  conexión del pool. */
const STORAGE_CONCURRENCY = 4;
/** Cuántas veces se vuelve a preguntar por `file_name` mientras el video termina
 *  de subirse. */
const MAX_INTENTOS = 10;

export interface RutasThumbnailVideo {
  /** `<empresa>/opt/<nombre>.png` — el thumbnail que escribe el backend. */
  opt: string;
  /** `<empresa>/full/<nombre>.png` — respaldo cuando el `opt/` todavía no está. */
  fullPng: string;
  /** `<empresa>/full/<nombre>.<ext>` — el video en sí. */
  full: string;
}

/**
 * Rutas en el bucket `generadas` para un video y su thumbnail. Sólo se reemplaza la
 * extensión FINAL (`.mp4` / `.webm` / `.mov`, sin importar mayúsculas), igual que
 * hace `fetchImages`; un nombre sin extensión de video queda tal cual.
 */
export function rutasThumbnailVideo(
  companyId: string,
  fileName: string,
): RutasThumbnailVideo {
  const png = fileName.replace(/\.(mp4|webm|mov)$/i, '.png');
  return {
    opt: `${companyId}/opt/${png}`,
    fullPng: `${companyId}/full/${png}`,
    full: `${companyId}/full/${fileName}`,
  };
}

const firmar = async (path: string): Promise<string | null> => {
  const cacheada = getCachedSignedUrl(path);
  if (cacheada) return cacheada;
  try {
    const { data } = await supabase.storage.from('generadas').createSignedUrl(path, 3600);
    if (!data?.signedUrl) return null;
    setCachedSignedUrl(path, data.signedUrl);
    return data.signedUrl;
  } catch {
    return null;
  }
};

const esperar = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Por cada id de `imagenes_generadas`: espera el `file_name` (el video puede tardar
 * en quedar escrito), firma el thumbnail (`opt/`, si no `full/*.png`) y el `.mp4`, y
 * precalienta el PNG en la cache del navegador. Los que quedan sin thumbnail se
 * descartan: una card sin imagen no se puede dibujar, y `cerrarTanda` con la lista
 * vacía ya pide recarga a la grilla.
 */
export async function preloadVideosDeTanda(
  ids: Array<number | string>,
  companyId: string,
): Promise<PreloadedImage[]> {
  const pendientes = new Set<number | string>(ids);
  const listos: PreloadedImage[] = [];

  for (let intento = 1; intento <= MAX_INTENTOS && pendientes.size > 0; intento++) {
    const resultados = await mapWithConcurrency(
      Array.from(pendientes),
      STORAGE_CONCURRENCY,
      async (id): Promise<PreloadedImage | null> => {
        try {
          const { data, error } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito, variante_id, variantes:variantes(nombre)')
            .eq('id', id)
            .eq('id_company', companyId)
            .maybeSingle();
          const fila = data as
            | {
                file_name?: string | null;
                favorito?: boolean | null;
                variante_id?: number | string | null;
                variantes?: { nombre?: string | null } | null;
              }
            | null;
          if (error || !fila?.file_name) return null;

          const rutas = rutasThumbnailVideo(companyId, fila.file_name);
          const [thumbnail, video] = await Promise.all([
            firmar(rutas.opt).then((u) => u ?? firmar(rutas.fullPng)),
            firmar(rutas.full),
          ]);
          // Sin thumbnail no hay card; sin `.mp4` sí (se abre por el visor, que firma
          // lo suyo). El `signedUrl` cae al thumbnail para respetar el tipo.
          if (!thumbnail) return null;
          return {
            id,
            fileName: fila.file_name,
            signedUrl: video ?? thumbnail,
            optSignedUrl: thumbnail,
            favorito: fila.favorito === true,
            variante_nombre: fila.variante_id
              ? fila.variantes?.nombre || undefined
              : undefined,
          };
        } catch (err) {
          console.error(`[preloadVideosDeTanda] error con el video ${id}:`, err);
          return null;
        }
      },
    );
    for (const r of resultados) {
      if (!r) continue;
      listos.push(r);
      pendientes.delete(r.id);
    }
    if (pendientes.size > 0 && intento < MAX_INTENTOS) await esperar(1500);
  }

  // Precalentar el PNG: es lo que la card muestra, y así entra sin parpadeo.
  await mapWithConcurrency(listos, STORAGE_CONCURRENCY, async (v) => {
    if (typeof Image === 'undefined' || !v.optSignedUrl) return;
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = v.optSignedUrl as string;
    });
  });

  return listos;
}

export default preloadVideosDeTanda;
